import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ViewOnlyBanner from "@/components/ViewOnlyBanner";
import api, { API, formatApiError, friendlyAxiosError, uploadFileSmart } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Pencil,
  Trash2,
  Upload,
  Sparkles,
  FileText,
  AlertTriangle,
  Mail,
  Paperclip,
  X,
  Search,
  Download,
  Eye,
  MessageSquare,
  Send,
  Quote,
  BookOpen,
  Loader2,
  ListChecks,
  ChevronDown,
  ChevronUp,
  Hammer,
} from "lucide-react";
import { useConfirm } from "@/context/ConfirmContext";

const STATUSES = [
  { value: "open", label: "Open", color: "#0088CC" },
  { value: "submitted", label: "Submitted", color: "#10B981" },
  { value: "won", label: "Won", color: "#22C55E" },
  { value: "lost", label: "Lost", color: "#EF4444" },
  { value: "withdrawn", label: "Withdrawn", color: "#94A3B8" },
];

// Visual metadata for activity-log entries.
const HISTORY_ACTION_META = {
  created: { label: "Created", color: "#0088CC" },
  updated: { label: "Updated", color: "#10B981" },
  uploaded: { label: "Uploaded", color: "#8B5CF6" },
  deleted_attachment: { label: "Removed file", color: "#EF4444" },
  client_created: { label: "New client added", color: "#22D3EE" },
};

function emptyTender() {
  return {
    title: "",
    client: "",
    due_date: "",
    submission_method: "",
    estimated_value: "",
    summary: "",
    deliverables: [],
    certifications: [],
    vessel_specs: [],
    vessels: [],
    trailer_required: false,
    risk_flags: [],
    key_dates: [],
    status: "open",
    notes: "",
    subscribers: [],
    attachments: [],
  };
}

// Interactive pre-lodgement checklist on each tender card. Items are built
// by the prep-guide AI (plus manual additions) and tick-state syncs live.
function TenderChecklist({ tender, canEdit }) {
  const [items, setItems] = useState(tender.checklist || []);
  const [open, setOpen] = useState(false);
  const [newText, setNewText] = useState("");
  useEffect(() => {
    setItems(tender.checklist || []);
  }, [tender.checklist]);
  const doneCount = items.filter((i) => i.done).length;
  if (!items.length && !canEdit) return null;

  const toggle = async (it) => {
    setItems((xs) => xs.map((x) => (x.id === it.id ? { ...x, done: !it.done } : x)));
    try {
      await api.patch(`/tenders/${tender.id}/checklist-items/${it.id}`, { done: !it.done });
    } catch {
      setItems((xs) => xs.map((x) => (x.id === it.id ? { ...x, done: it.done } : x)));
      toast.error("Couldn't update the checklist item");
    }
  };
  const add = async () => {
    const text = newText.trim();
    if (!text) return;
    setNewText("");
    try {
      const { data } = await api.post(`/tenders/${tender.id}/checklist-items`, { text });
      setItems((xs) => [...xs, data]);
    } catch {
      toast.error("Couldn't add the item");
    }
  };
  const remove = async (it) => {
    setItems((xs) => xs.filter((x) => x.id !== it.id));
    try {
      await api.delete(`/tenders/${tender.id}/checklist-items/${it.id}`);
    } catch {
      setItems((xs) => [...xs, it]);
      toast.error("Couldn't remove the item");
    }
  };

  let lastGroup = null;
  return (
    <div
      className="mt-3 rounded border border-[#DDE5EC] bg-white/70"
      onClick={(e) => e.stopPropagation()}
      data-testid={`tender-checklist-${tender.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-left"
        data-testid={`tender-checklist-toggle-${tender.id}`}
      >
        <ListChecks className="h-3.5 w-3.5 text-[#0088CC] shrink-0" />
        <span className="text-[10px] uppercase tracking-wider text-[#5B6B7C] font-display font-semibold">
          Checklist
        </span>
        <span className="text-[11px] font-semibold text-[#011836] tabular-nums" data-testid={`tender-checklist-count-${tender.id}`}>
          {doneCount}/{items.length}
        </span>
        <div className="flex-1 h-1.5 rounded-full bg-[#E8EEF3] overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${doneCount === items.length && items.length > 0 ? "bg-[#22C55E]" : "bg-[#0088CC]"}`}
            style={{ width: items.length ? `${(doneCount / items.length) * 100}%` : "0%" }}
          />
        </div>
        {open ? (
          <ChevronUp className="h-3.5 w-3.5 text-[#5B6B7C] shrink-0" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-[#5B6B7C] shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-3 pb-3">
          {items.length === 0 && (
            <div className="text-[11px] text-[#5B6B7C] mb-2">
              No items yet — press the guide button to build the checklist from the tender
              documents, or add items below.
            </div>
          )}
          <div className="max-h-56 overflow-y-auto pr-1 space-y-0.5">
            {items.map((it) => {
              const showGroup = it.group && it.group !== lastGroup;
              lastGroup = it.group || lastGroup;
              return (
                <div key={it.id}>
                  {showGroup && (
                    <div className="text-[9px] uppercase tracking-wider text-[#5B6B7C] font-semibold mt-2 mb-0.5">
                      {it.group}
                    </div>
                  )}
                  <div className="flex items-start gap-2 group/item rounded px-1 py-0.5 hover:bg-[#F4F8FB]">
                    <input
                      type="checkbox"
                      checked={!!it.done}
                      disabled={!canEdit}
                      onChange={() => toggle(it)}
                      className="mt-0.5 h-3.5 w-3.5 rounded border-[#C4D0DB] text-[#0088CC] focus:ring-[#0088CC]/60 cursor-pointer shrink-0"
                      data-testid={`checklist-check-${it.id}`}
                    />
                    <div className="min-w-0 flex-1 text-xs leading-snug">
                      <span className={it.done ? "line-through text-[#9AA8B5]" : "text-[#011836]"}>
                        {it.text}
                      </span>
                      {it.exclusion_risk && (
                        <span className="ml-1.5 text-[9px] font-bold text-red-600 uppercase">
                          exclusion risk
                        </span>
                      )}
                      {it.owner && (
                        <span className="ml-1.5 text-[10px] text-[#64748B]">· {it.owner}</span>
                      )}
                    </div>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => remove(it)}
                        className="opacity-0 group-hover/item:opacity-100 text-[#9AA8B5] hover:text-red-600 shrink-0"
                        title="Remove item"
                        data-testid={`checklist-remove-${it.id}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {canEdit && (
            <div className="flex gap-2 mt-2">
              <Input
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && add()}
                placeholder="Add your own item…"
                className="h-7 bg-white border-[#DDE5EC] text-[#011836] text-xs"
                data-testid={`checklist-add-input-${tender.id}`}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={add}
                className="h-7 px-2.5 border-[#C4D0DB] text-[#33475C] hover:text-[#0088CC] hover:border-[#0088CC]/50"
                data-testid={`checklist-add-btn-${tender.id}`}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function statusMeta(value) {
  return STATUSES.find((s) => s.value === value) || STATUSES[0];
}

function daysFromNow(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / 86_400_000);
}

// Look up a value from a tender's vessel_specs list by partial label match
// (case-insensitive). Accepts an array of keywords so we can capture
// synonyms (e.g. BEAM / BREADTH refer to the same dimension).
function specValue(specs, keywords) {
  if (!Array.isArray(specs)) return "";
  for (const sp of specs) {
    const label = (sp?.label || "").toUpperCase();
    if (keywords.some((k) => label.includes(k))) {
      return (sp?.value || "").trim();
    }
  }
  return "";
}

// Friendly heading label for grouping attachments by upload day.
function formatDayLabel(iso) {
  if (!iso) return "Earlier";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Earlier";
  const today = new Date();
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(today) - startOfDay(d)) / 86_400_000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

// Friendlier date+time for the activity log (more precise than the day label).
function formatLogTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// Renders a key-date value in a calendar-friendly format. Accepts:
//   "2026-06-24"                       → "Wed 24 Jun"
//   "2026-06-24T10:00:00+10:00"        → "Wed 24 Jun · 10:00"
// Falls back to the raw string if it can't be parsed.
function formatKeyDate(raw) {
  if (!raw) return "";
  // Pure-date strings should be rendered without timezone shifts (a "24 Jun"
  // event must read as "24 Jun" no matter what TZ the browser is in).
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);
  const d = new Date(isDateOnly ? `${raw}T00:00:00` : raw);
  if (Number.isNaN(d.getTime())) return raw;
  const dayLabel = d.toLocaleDateString(undefined, {
    weekday: "short", day: "numeric", month: "short",
  });
  if (isDateOnly) return dayLabel;
  return `${dayLabel} · ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
}

// Days until a key-date — used to colour-code (overdue red, upcoming amber).
function daysUntilKeyDate(raw) {
  if (!raw) return null;
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);
  const d = new Date(isDateOnly ? `${raw}T00:00:00` : raw);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000);
}

export default function Tenders() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || (user?.role === "staff" && user?.can_edit);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");

  // Deep-link from the dashboard's "Upcoming tender dates" panel:
  // /tenders?focus={id} — show all statuses, scroll to the card, flash it.
  const focusId = new URLSearchParams(window.location.search).get("focus");
  const focusedOnce = useRef(false);
  useEffect(() => {
    if (focusId && !focusedOnce.current) setStatusFilter("all");
  }, [focusId]);
  useEffect(() => {
    if (!focusId || focusedOnce.current) return;
    const el = document.querySelector(`[data-testid="tender-card-${focusId}"]`);
    if (el) {
      focusedOnce.current = true;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-[#0088CC]");
      setTimeout(() => el.classList.remove("ring-2", "ring-[#0088CC]"), 2500);
    }
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // tender id or null for new
  const [form, setForm] = useState(emptyTender());
  const [subscriberInput, setSubscriberInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [extractingPath, setExtractingPath] = useState(null);
  // Q&A panel state — let the user ask Claude questions about the tender
  // attachments. `qaInput` is the current text field, `qaSending` toggles
  // the spinner, and `qaHistory` mirrors the persisted history on the
  // tender so the conversation survives a reload.
  const [qaInput, setQaInput] = useState("");
  const [qaSending, setQaSending] = useState(false);
  const [qaHistory, setQaHistory] = useState([]);
  const [briefingReport, setBriefingReport] = useState(null);
  const [briefingSending, setBriefingSending] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenders");
      setList(data);
    } catch (e) {
      if (e?.response?.status && e.response.status !== 401) {
        toast.error("Could not load tenders");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Standard load-on-mount idiom — wrapped in a microtask to satisfy lint
    // rules that flag synchronous state setters in useEffect bodies.
    Promise.resolve().then(load);
  }, []);

  // Deep-link handler: when the URL contains ?open={tenderId} (used by the
  // weekly digest email's "Open tender →" links), auto-open the edit drawer
  // for that tender once the list has loaded.
  useEffect(() => {
    if (!list.length) return;
    const params = new URLSearchParams(window.location.search);
    const openId = params.get("open");
    if (!openId) return;
    const found = list.find((t) => t.id === openId);
    if (found) {
      openEdit(found);
      // Clean the URL so a refresh doesn't re-open the dialog on every visit.
      const url = new URL(window.location.href);
      url.searchParams.delete("open");
      window.history.replaceState({}, "", url.toString());
    }
  }, [list]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyTender());
    setSubscriberInput("");
    setQaInput("");
    setQaHistory([]);
    setModalOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t.id);
    // Coerce legacy string vessel_specs (created before the structured list
    // shape) into a single-row list so the form renders consistently.
    let specs = t.vessel_specs;
    if (typeof specs === "string") {
      specs = specs.trim() ? [{ label: "Notes", value: specs.trim() }] : [];
    } else if (!Array.isArray(specs)) {
      specs = [];
    }
    setForm({
      ...emptyTender(),
      ...t,
      deliverables: t.deliverables || [],
      certifications: t.certifications || [],
      risk_flags: t.risk_flags || [],
      key_dates: t.key_dates || [],
      subscribers: t.subscribers || [],
      attachments: t.attachments || [],
      vessel_specs: specs,
      vessels: t.vessels || [],
      trailer_required: t.trailer_required === true,
      history: t.history || [],
    });
    setSubscriberInput("");
    setQaInput("");
    setQaHistory(Array.isArray(t.qa_history) ? t.qa_history : []);
    setModalOpen(true);
  };

  const close = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    try {
      const body = { ...form };
      const isNew = !editing;
      let saved;
      if (editing) {
        const { data } = await api.put(`/tenders/${editing}`, body);
        saved = data;
        setList((xs) => xs.map((x) => (x.id === editing ? data : x)));
        toast.success("Tender updated");
      } else {
        const { data } = await api.post("/tenders", body);
        saved = data;
        setList((xs) => [data, ...xs]);
        toast.success("Tender created");
      }
      // Ask before spamming the subscriber list. Skip the prompt entirely
      // if there are no subscribers on this tender (nothing to send).
      const subs = saved?.subscribers || [];
      if (subs.length > 0) {
        const yes = await confirm({
          title: `Notify ${subs.length} subscriber${subs.length === 1 ? "" : "s"}?`,
          description: `Would you like to email the ${subs.length} subscriber${
            subs.length === 1 ? "" : "s"
          } on this tender about the ${isNew ? "new tender" : "update"}?`,
          confirmText: "Yes, send email",
          cancelText: "No, don't send",
        });
        if (yes) {
          try {
            const { data: r } = await api.post(
              `/tenders/${saved.id}/notify-subscribers?event=${isNew ? "created" : "updated"}`,
            );
            toast.success(
              `Notification sent to ${r?.sent ?? subs.length} subscriber${
                (r?.sent ?? subs.length) === 1 ? "" : "s"
              }`,
            );
          } catch (err) {
            toast.error(
              formatApiError(err.response?.data?.detail) ||
                "Save succeeded but notification failed. Try again from the tender.",
            );
          }
        }
      }
      close();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (t) => {
    const ok = await confirm({
      title: `Delete tender "${t.title || t.client || "Untitled"}"?`,
      description: "This permanently removes the tender and its file references. The original uploads stay in storage.",
      confirmText: "Delete tender",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/tenders/${t.id}`);
      setList((xs) => xs.filter((x) => x.id !== t.id));
      toast.success("Tender deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const onUploadFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = ""; // allow re-upload of same names
    setUploading(true);
    let successCount = 0;
    let failCount = 0;
    let aiFilled = 0;
    // Upload sequentially so the backend isn't slammed and the UI can update
    // attachments one-at-a-time. After each successful upload we also run
    // the AI extractor on that file so the fields auto-populate. The
    // extractor only fills EMPTY fields, so file 1 establishes the baseline
    // and subsequent files top up any gaps without overwriting user edits.
    for (const file of files) {
      let att = null;
      try {
        // Chunk-aware upload — big tender PDFs are sent in 4MB slices so the
        // production proxy's body-size cap can't reject them (up to 50MB).
        const data = await uploadFileSmart(file);
        att = {
          storage_path: data.path,
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          size: file.size || 0,
          uploaded_at: new Date().toISOString(),
        };
        setForm((f) => ({
          ...f,
          attachments: [...(f.attachments || []), att],
        }));
        successCount += 1;
      } catch (err) {
        failCount += 1;
        toast.error(`${file.name}: ${friendlyAxiosError(err)}`);
        continue;
      }
      // Auto-extract — silent (no per-file toast). If the file isn't AI-readable
      // (e.g. a blank scan), we swallow the error here; the user can still
      // click Extract manually. Runs as a background job so a slow LLM call
      // can't time out the Cloudflare edge.
      try {
        setExtractingPath(att.storage_path);
        const { data: kicker } = await api.post("/tenders/ai/extract", {
          storage_path: att.storage_path,
        });
        const ai = await pollAiJob(kicker.job_id);
        setForm((f) => ({
          ...f,
          title: f.title || ai.title,
          client: f.client || ai.client,
          due_date: f.due_date || ai.due_date,
          submission_method: f.submission_method || ai.submission_method,
          estimated_value: f.estimated_value || ai.estimated_value,
          summary: f.summary || ai.summary,
          deliverables: f.deliverables?.length ? f.deliverables : ai.deliverables || [],
          certifications: f.certifications?.length ? f.certifications : ai.certifications || [],
          vessel_specs: f.vessel_specs?.length ? f.vessel_specs : ai.vessel_specs || [],
          vessels: f.vessels?.length ? f.vessels : ai.vessels || [],
          // Trailer tickbox: only flip ON via AI. Once a user has set it
          // (either way), leave their value alone on subsequent extracts.
          trailer_required: f.trailer_required || ai.trailer_required === true,
          risk_flags: f.risk_flags?.length ? f.risk_flags : ai.risk_flags || [],
          // Key dates: merge by (date, label) signature so multi-file
          // uploads union the obligations without duplicating.
          key_dates: (() => {
            const existing = f.key_dates || [];
            const aiKd = ai.key_dates || [];
            const sig = (k) => `${(k.date || "").toLowerCase()}|${(k.label || "").toLowerCase()}`;
            const seen = new Set(existing.map(sig));
            const merged = [...existing];
            for (const k of aiKd) {
              if (!seen.has(sig(k))) {
                merged.push(k);
                seen.add(sig(k));
              }
            }
            return merged;
          })(),
        }));
        aiFilled += 1;
      } catch {
        // Silent — the file is still uploaded and the user can hit Extract manually.
      } finally {
        setExtractingPath(null);
      }
    }
    setUploading(false);
    if (successCount > 0) {
      const aiNote =
        aiFilled > 0
          ? ` — AI filled ${aiFilled === successCount ? "the" : `${aiFilled} of ${successCount}`} file${aiFilled === 1 ? "" : "s"}`
          : "";
      toast.success(
        files.length === 1
          ? `Uploaded ${files[0].name}${aiNote}`
          : `Uploaded ${successCount} file${successCount === 1 ? "" : "s"}${aiNote}`,
      );
    }
  };

  const removeAttachment = async (idx) => {
    const att = form.attachments[idx];
    const ok = await confirm({
      title: `Remove "${att?.filename || "this document"}"?`,
      description:
        "The file reference is removed from this tender. It stays in storage but won't be linked here anymore.",
      confirmText: "Remove",
      destructive: true,
    });
    if (!ok) return;
    setForm((f) => ({
      ...f,
      attachments: f.attachments.filter((_, i) => i !== idx),
    }));
  };

  const runAiExtract = async (att) => {
    setExtractingPath(att.storage_path);
    try {
      const { data: kicker } = await api.post("/tenders/ai/extract", {
        storage_path: att.storage_path,
      });
      const data = await pollAiJob(kicker.job_id);
      // Fill ONLY empty fields — never overwrite user edits.
      setForm((f) => ({
        ...f,
        title: f.title || data.title,
        client: f.client || data.client,
        due_date: f.due_date || data.due_date,
        submission_method: f.submission_method || data.submission_method,
        estimated_value: f.estimated_value || data.estimated_value,
        summary: f.summary || data.summary,
        deliverables: f.deliverables?.length ? f.deliverables : data.deliverables || [],
        certifications: f.certifications?.length ? f.certifications : data.certifications || [],
        vessel_specs: f.vessel_specs?.length ? f.vessel_specs : data.vessel_specs || [],
        vessels: f.vessels?.length ? f.vessels : data.vessels || [],
        trailer_required: f.trailer_required || data.trailer_required === true,
        risk_flags: f.risk_flags?.length ? f.risk_flags : data.risk_flags || [],
        // Merge key dates so manual Extract on a 2nd doc unions with the
        // dates the 1st doc already produced.
        key_dates: (() => {
          const existing = f.key_dates || [];
          const aiKd = data.key_dates || [];
          const sig = (k) => `${(k.date || "").toLowerCase()}|${(k.label || "").toLowerCase()}`;
          const seen = new Set(existing.map(sig));
          const merged = [...existing];
          for (const k of aiKd) {
            if (!seen.has(sig(k))) {
              merged.push(k);
              seen.add(sig(k));
            }
          }
          return merged;
        })(),
      }));
      toast.success("AI extracted tender fields");
    } catch (err) {
      toast.error(err.message || formatApiError(err.response?.data?.detail) || "AI extract failed");
    } finally {
      setExtractingPath(null);
    }
  };

  // Tender Preparation Guide — AI-written .docx built from the tender's
  // documents. Cached server-side against the attachment set, so repeat
  // presses return the identical file until documents change.
  const [guideBusy, setGuideBusy] = useState(null);
  const downloadGuide = (g) => {
    const a = document.createElement("a");
    a.href = `${API}/files/${g.storage_path}?download=1`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  // Won tender → build project (key dates carried across as milestones).
  const navigate = useNavigate();
  const [convertingId, setConvertingId] = useState(null);
  const convertToProject = async (t) => {
    if (convertingId) return;
    setConvertingId(t.id);
    try {
      const { data } = await api.post(`/tenders/${t.id}/convert-to-project`);
      toast.success("Project created — key dates carried across");
      navigate(`/projects/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't convert this tender");
    } finally {
      setConvertingId(null);
    }
  };

  const generateGuide = async (t) => {
    if (guideBusy) return;
    if (!(t.attachments || []).length) {
      toast.error("Upload the tender documents first — the guide is written from them.");
      return;
    }
    setGuideBusy(t.id);
    try {
      const { data } = await api.post(`/tenders/${t.id}/prep-guide`);
      if (data.status === "ready") {
        downloadGuide(data);
        toast.success(
          data.cached
            ? "Preparation guide downloaded — unchanged since documents were last updated"
            : "Preparation guide downloaded"
        );
      } else {
        toast.info("Writing the preparation guide from the tender documents — this can take a couple of minutes…");
        const result = await pollAiJob(data.job_id, { intervalMs: 5000, maxAttempts: 90 });
        downloadGuide(result);
        toast.success("Preparation guide ready — downloading");
        load();
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Guide generation failed");
    } finally {
      setGuideBusy(null);
    }
  };

  // Generic polling helper for AI background jobs. The kicker endpoint
  // returns `{job_id, status: "pending"}`; we poll `GET /ai-jobs/<id>` every
  // 3 seconds until `status` becomes "done" (return result) or "failed"
  // (throw). Up to ~3 minutes total — matches the backend's hard 180s cap.
  const pollAiJob = async (jobId, { intervalMs = 3000, maxAttempts = 60 } = {}) => {
    let lastErr = null;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, intervalMs));
      try {
        const { data: job } = await api.get(`/ai-jobs/${jobId}`);
        if (job.status === "done") return job.result;
        if (job.status === "failed") throw new Error(job.error || "AI job failed");
      } catch (e) {
        // Transient poll failure — keep going. Only surface at the end.
        lastErr = e;
      }
    }
    throw lastErr || new Error("AI job timed out");
  };

  // Ask the AI a question about the tender's attachments. Answer comes
  // back with citations (document + page + verbatim quote). Persisted on
  // the tender server-side so refreshing keeps the history.
  const askQuestion = async () => {
    const q = qaInput.trim();
    if (!q) return;
    if (!editing) {
      toast.error("Save the tender before asking questions");
      return;
    }
    if (!(form.attachments || []).length) {
      toast.error("Upload at least one document first");
      return;
    }
    setQaSending(true);
    try {
      const { data } = await api.post(`/tenders/${editing}/ask`, { question: q });
      setQaHistory((h) => [data, ...h]);
      setQaInput("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Ask failed");
    } finally {
      setQaSending(false);
    }
  };

  const deleteQa = async (qaId) => {
    if (!editing) return;
    try {
      await api.delete(`/tenders/${editing}/ask/${qaId}`);
      setQaHistory((h) => h.filter((x) => x.id !== qaId));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't remove");
    }
  };

  // One-click bulk re-scan of every attachment to harvest key dates.
  // Runs as a background job on the backend (single sync HTTP call would
  // exceed Cloudflare's ~100s edge timeout on tenders with many attachments).
  // We poll the job every 3 seconds until it completes, then merge results.
  const [refreshingKeyDates, setRefreshingKeyDates] = useState(false);
  const refreshKeyDates = async () => {
    if (!editing) {
      toast.error("Save the tender before refreshing");
      return;
    }
    if (!(form.attachments || []).length) {
      toast.error("Upload at least one document first");
      return;
    }
    setRefreshingKeyDates(true);
    try {
      const { data: kicker } = await api.post(`/tenders/${editing}/refresh-key-dates`);
      const result = await pollAiJob(kicker.job_id);

      setForm((f) => ({ ...f, key_dates: result.key_dates || [] }));
      setList((xs) =>
        xs.map((x) => (x.id === editing ? { ...x, key_dates: result.key_dates || [] } : x))
      );
      if (result.added > 0) {
        toast.success(
          `Added ${result.added} new key date${result.added === 1 ? "" : "s"} from ${result.scanned_files.length} document${result.scanned_files.length === 1 ? "" : "s"}`
        );
      } else if (result.ai_found > 0) {
        toast.info("All AI-detected dates were already on the list");
      } else {
        toast.info("AI didn't find any mandatory meetings or deadlines in the attachments");
      }
    } catch (err) {
      toast.error(err.message || formatApiError(err.response?.data?.detail) || "Refresh failed");
    } finally {
      setRefreshingKeyDates(false);
    }
  };

  const addSubscriber = () => {
    const e = subscriberInput.trim().toLowerCase();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (form.subscribers.includes(e)) {
      toast.error("Already in the subscriber list");
      return;
    }
    setForm((f) => ({ ...f, subscribers: [...f.subscribers, e] }));
    setSubscriberInput("");
  };

  const removeSubscriber = (email) =>
    setForm((f) => ({ ...f, subscribers: f.subscribers.filter((x) => x !== email) }));

  // ------------------------- list filtering
  const q = search.trim().toLowerCase();
  const filtered = list.filter((t) => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (!q) return true;
    const blob = `${t.title} ${t.client} ${t.summary}`.toLowerCase();
    return blob.includes(q);
  });

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto" data-testid="tenders-page">
      <ViewOnlyBanner />
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#011836]">Tenders</h1>
          <p className="text-sm text-[#5B6B7C] mt-1">
            Upload tender docs, auto-extract key fields, and keep your team in the loop.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Admin-only: Monday briefing controls. Sends the same consolidated
              email that auto-fires Monday 08:00 (Australia/Sydney) and shows a
              per-recipient delivery report, or opens the live preview. */}
          {(user?.role === "admin") && (
            <>
              <Button
                onClick={() => window.open(`${API}/monday-briefing/preview`, "_blank")}
                variant="outline"
                className="border-[#0088CC]/40 text-[#0088CC] hover:bg-[#0088CC]/10"
                data-testid="briefing-preview-btn"
                title="Open the rendered HTML of the Monday briefing in a new tab"
              >
                <Eye className="h-4 w-4 mr-2" /> Preview briefing
              </Button>
              <Button
                onClick={async () => {
                  setBriefingSending(true);
                  try {
                    const { data } = await api.post("/monday-briefing/send");
                    if (data.error) throw new Error(data.error);
                    if (data.skipped === "no_recipients") {
                      toast.info("No staff recipients configured — nothing to send.");
                      return;
                    }
                    const sent = data.sent || [];
                    const failed = sent.filter((r) => !r.delivered);
                    if (failed.length) {
                      toast.error(`${failed.length} of ${sent.length} emails failed — see the delivery report`);
                    } else {
                      toast.success(`Briefing sent to ${sent.length} recipient${sent.length === 1 ? "" : "s"}`);
                    }
                    setBriefingReport(sent);
                  } catch (err) {
                    toast.error(err.message || formatApiError(err.response?.data?.detail) || "Send failed");
                  } finally {
                    setBriefingSending(false);
                  }
                }}
                disabled={briefingSending}
                variant="outline"
                className="border-[#FBBF24]/40 text-[#FBBF24] hover:bg-[#FBBF24]/10"
                data-testid="briefing-send-btn"
                title="Send the Monday briefing to all staff & admins right now"
              >
                <Send className="h-4 w-4 mr-2" /> {briefingSending ? "Sending…" : "Send briefing now"}
              </Button>
            </>
          )}
          {canEdit && (
            <Button
              onClick={openNew}
              className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
              data-testid="new-tender-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> New tender
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5B6B7C]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, client, summary…"
            className="pl-9 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
            data-testid="tender-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATUSES.map((s) => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-display font-semibold uppercase tracking-wider border transition-colors ${
                statusFilter === s.value
                  ? "text-[#011836]"
                  : "border-[#DDE5EC] text-[#5B6B7C] hover:text-[#011836]"
              }`}
              style={
                statusFilter === s.value
                  ? { backgroundColor: s.color, borderColor: s.color }
                  : undefined
              }
              data-testid={`filter-${s.value}`}
            >
              {s.label}
            </button>
          ))}
          <button
            onClick={() => setStatusFilter("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-display font-semibold uppercase tracking-wider border transition-colors ${
              statusFilter === "all"
                ? "bg-[#0088CC] border-[#0088CC] text-white"
                : "border-[#DDE5EC] text-[#5B6B7C] hover:text-[#011836]"
            }`}
            data-testid="filter-all"
          >
            All
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-[#5B6B7C] py-12 text-center">Loading tenders…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-[#DDE5EC] bg-white py-16 px-6 text-center" data-testid="tenders-empty">
          <FileSearchIcon />
          <div className="mt-3 text-[#011836] font-display tracking-wide">
            {list.length === 0 ? "No tenders yet" : "Nothing matches your filters"}
          </div>
          <div className="text-sm text-[#5B6B7C] mt-1">
            {list.length === 0
              ? "Click ‘New tender’ to add your first one."
              : "Try clearing the search or status filter."}
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="tenders-list">
          {filtered.map((t) => {
            const meta = statusMeta(t.status);
            const days = daysFromNow(t.due_date);
            const attachments = t.attachments || [];
            const vessels = t.vessels || [];
            return (
              <div
                key={t.id}
                className="rounded-md border border-[#DDE5EC] bg-white hover:border-[#C4D0DB] transition-colors cursor-pointer"
                data-testid={`tender-card-${t.id}`}
                onClick={() => openEdit(t)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openEdit(t);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {/* Two-column row layout: main content (left) + scrollable
                    attachments dock (right). Stack vertically on small
                    screens. Outer height is driven by content. */}
                <div className="flex flex-col md:flex-row">
                  <div className="flex-1 min-w-0 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className="text-[10px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-[#011836]"
                            style={{ backgroundColor: meta.color }}
                          >
                            {meta.label}
                          </span>
                          {t.trailer_required && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-display font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#0088CC]/15 text-[#0088CC] border border-[#0088CC]/30"
                              title="Trailer / road-transport requirement detected by AI"
                              data-testid={`tender-trailer-chip-${t.id}`}
                            >
                              Trailer
                            </span>
                          )}
                          {t.risk_flags && t.risk_flags.length > 0 && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] text-[#FBBF24]"
                              title={t.risk_flags.join("; ")}
                            >
                              <AlertTriangle className="h-3 w-3" /> {t.risk_flags.length} flag{t.risk_flags.length === 1 ? "" : "s"}
                            </span>
                          )}
                        </div>
                        <div
                          className="font-display text-base font-semibold text-[#011836] mt-2 leading-snug"
                          data-testid={`tender-title-${t.id}`}
                        >
                          {t.title || "(untitled)"}
                        </div>
                        {t.client && (
                          <div className="text-xs text-[#5B6B7C] mt-0.5">{t.client}</div>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {t.status === "won" && canEdit && (
                          <button
                            onClick={(e) => { e.stopPropagation(); convertToProject(t); }}
                            disabled={convertingId === t.id}
                            className="px-2 py-1.5 rounded inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#0B6E4F] bg-[#E7F6EF] hover:bg-[#D2EEDF] disabled:opacity-60"
                            data-testid={`tender-to-project-${t.id}`}
                            title="Convert this won tender into a project — key dates become milestones"
                          >
                            {convertingId === t.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Hammer className="h-3.5 w-3.5" />
                            )}
                            To project
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); generateGuide(t); }}
                          disabled={guideBusy === t.id}
                          className="p-1.5 rounded text-[#5B6B7C] hover:text-[#0088CC] hover:bg-[#E6F4FB] disabled:opacity-60"
                          data-testid={`tender-prep-guide-${t.id}`}
                          title="Download the Tender Preparation Guide (written by AI from this tender's documents)"
                        >
                          {guideBusy === t.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <BookOpen className="h-3.5 w-3.5" />
                          )}
                        </button>
                        {canEdit && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                              className="p-1.5 rounded text-[#5B6B7C] hover:text-[#011836] hover:bg-[#EDF1F5]"
                              data-testid={`edit-tender-${t.id}`}
                              title="Open tender"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); remove(t); }}
                              className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                              data-testid={`delete-tender-${t.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {t.summary && (
                      <p className="text-xs text-[#33475C] mt-3 leading-relaxed line-clamp-2">
                        {t.summary}
                      </p>
                    )}

                    {/* Multi-vessel briefs — each one a stacked line. Only
                        renders for tenders the AI flagged as multi-vessel. */}
                    {vessels.length > 0 && (
                      <div className="mt-3 space-y-1" data-testid={`tender-vessels-${t.id}`}>
                        {vessels.map((v, i) => (
                          <div key={i} className="flex items-baseline gap-2 text-xs leading-relaxed">
                            <span className="text-[10px] uppercase tracking-wider text-[#0088CC] font-display font-semibold shrink-0">
                              {v.label || `Vessel ${i + 1}`}
                            </span>
                            <span className="text-[#33475C]">{v.summary}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Mandatory events / key dates — site visits, briefings,
                        Q&A deadlines, etc. Surfaced on the card so the team
                        can spot must-attend fixtures without opening the
                        tender. Sorted soonest-first; overdue items pushed to
                        the end. */}
                    {Array.isArray(t.key_dates) && t.key_dates.length > 0 && (
                      <div
                        className="mt-3 rounded border border-[#FBBF24]/30 bg-[#FBBF24]/[0.06] px-3 py-2.5"
                        data-testid={`tender-key-dates-${t.id}`}
                      >
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#FBBF24] font-display font-semibold flex items-center gap-1.5 mb-1.5">
                          <AlertTriangle className="h-3 w-3" /> Key dates
                        </div>
                        <ul className="space-y-1">
                          {[...t.key_dates]
                            .map((k, idx) => ({ ...k, _idx: idx, _days: daysUntilKeyDate(k.date) }))
                            .sort((a, b) => {
                              const ax = a._days ?? Number.POSITIVE_INFINITY;
                              const bx = b._days ?? Number.POSITIVE_INFINITY;
                              const overdue = (n) => (n != null && n < 0 ? 1 : 0);
                              return overdue(ax) - overdue(bx) || ax - bx;
                            })
                            .map((k) => {
                              const overdue = k._days !== null && k._days < 0;
                              const soon = k._days !== null && k._days >= 0 && k._days <= 7;
                              return (
                                <li
                                  key={k._idx}
                                  className="text-xs text-[#334155] leading-relaxed"
                                >
                                  <div className="flex items-baseline gap-2">
                                    <span
                                      className={`font-display font-semibold tabular-nums shrink-0 ${
                                        overdue ? "text-[#EF4444]" : soon ? "text-[#FBBF24]" : "text-[#011836]"
                                      }`}
                                    >
                                      {formatKeyDate(k.date) || "Date TBC"}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                      {k.mandatory !== false && (
                                        <span className="inline-block text-[9px] uppercase tracking-wider text-[#0b0e12] bg-[#FBBF24] px-1.5 py-0.5 rounded mr-1.5 align-middle font-display font-bold">
                                          Mandatory
                                        </span>
                                      )}
                                      <span className="text-[#011836]">{k.label}</span>
                                      {k.location && (
                                        <span className="text-[#5B6B7C]"> · {k.location}</span>
                                      )}
                                    </span>
                                  </div>
                                  {/* Registration block — only renders when the
                                      AI (or a human editor) populated at least
                                      one registration field. Kept compact so
                                      the card stays scannable. */}
                                  {(k.register_url || k.register_instructions) && (
                                    <div className="mt-1 ml-[6.25rem] flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[#5B6B7C]">
                                      <span className="text-[9px] uppercase tracking-[0.18em] text-[#FBBF24]/80 font-display font-semibold">
                                        Register
                                      </span>
                                      {k.register_url && (
                                        <a
                                          href={k.register_url}
                                          target="_blank"
                                          rel="noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-[#0088CC] hover:underline truncate max-w-[18rem]"
                                          data-testid={`tender-key-date-register-link-${t.id}-${k._idx}`}
                                          title={k.register_url}
                                        >
                                          {k.register_url.replace(/^mailto:/i, "")}
                                        </a>
                                      )}
                                      {k.register_instructions && (
                                        <span className="text-[#33475C]">
                                          {k.register_instructions}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {/* Source pointer — which uploaded doc this
                                      came from, plus the section if known.
                                      Helps the team verify quickly. */}
                                  {(k.source_document || k.source_section) && (
                                    <div className="mt-0.5 ml-[6.25rem] text-[10px] text-[#64748B]">
                                      Source:{" "}
                                      {k.source_document && (
                                        <span className="text-[#5B6B7C]">{k.source_document}</span>
                                      )}
                                      {k.source_document && k.source_section && " · "}
                                      {k.source_section && (
                                        <span className="text-[#5B6B7C]">{k.source_section}</span>
                                      )}
                                    </div>
                                  )}
                                </li>
                              );
                            })}
                        </ul>
                      </div>
                    )}

                    <TenderChecklist tender={t} canEdit={canEdit} />

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 text-xs">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">Due</div>
                        <div
                          className={`font-display font-semibold mt-0.5 ${
                            days !== null && days <= 7 && days >= 0
                              ? "text-[#FBBF24]"
                              : days !== null && days < 0
                              ? "text-[#EF4444]"
                              : "text-[#011836]"
                          }`}
                        >
                          {t.due_date
                            ? `${t.due_date}${days !== null ? `  (${days >= 0 ? `${days}d` : `${Math.abs(days)}d ago`})` : ""}`
                            : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">Value</div>
                        <div className="font-display font-semibold text-[#011836] mt-0.5 truncate">
                          {t.estimated_value || "—"}
                        </div>
                      </div>
                      {/* Only show the single-vessel spec strip when there is NO
                          multi-vessel list — otherwise the data is redundant. */}
                      {vessels.length === 0 && (
                        <>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">Length</div>
                            <div className="font-display font-semibold text-[#011836] mt-0.5 truncate" data-testid={`tender-card-length-${t.id}`}>
                              {specValue(t.vessel_specs, ["LENGTH OVERALL", "LENGTH"]) || "—"}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">Breadth</div>
                            <div className="font-display font-semibold text-[#011836] mt-0.5 truncate" data-testid={`tender-card-breadth-${t.id}`}>
                              {specValue(t.vessel_specs, ["BREADTH", "BEAM"]) || "—"}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">Material</div>
                            <div className="font-display font-semibold text-[#011836] mt-0.5 truncate" data-testid={`tender-card-material-${t.id}`}>
                              {specValue(t.vessel_specs, ["MATERIAL", "HULL CONSTRUCTION", "CONSTRUCTION", "HULL"]) || "—"}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-3 text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                      <span>
                        {attachments.length} file{attachments.length === 1 ? "" : "s"}
                      </span>
                      {t.subscribers && t.subscribers.length > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Mail className="h-3 w-3" /> {t.subscribers.length}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right-side attachments dock. Fixed cap on the height so
                      tall lists scroll inside the dock — keeps every row a
                      consistent visual size. Hidden when there are no
                      attachments to avoid an empty column. */}
                  {(attachments.length > 0 || t.prep_guide?.storage_path) && (
                    <div
                      className="md:w-72 md:min-w-[18rem] md:max-w-xs md:border-l border-t md:border-t-0 border-[#DDE5EC] bg-[#F6F7F8]/60 p-3 md:p-3.5"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`tender-attachments-${t.id}`}
                    >
                      <div className="text-[10px] uppercase tracking-wider text-[#5B6B7C] font-display font-semibold mb-2 flex items-center gap-1.5">
                        <Paperclip className="h-3 w-3" /> Attachments
                      </div>
                      <div className="max-h-[200px] overflow-y-auto pr-1 space-y-1.5">
                        {t.prep_guide?.storage_path && (
                          <a
                            href={`${API}/files/${t.prep_guide.storage_path}?download=1`}
                            rel="noreferrer"
                            className="block rounded border border-[#0088CC]/40 bg-[#E6F4FB]/70 hover:border-[#0088CC] hover:bg-[#0088CC]/[0.12] px-2.5 py-2 text-xs text-[#011836] transition-colors"
                            title="Download the generated Tender Preparation Guide"
                            data-testid={`tender-guide-attachment-${t.id}`}
                          >
                            <div className="flex items-start gap-2">
                              <BookOpen className="h-3.5 w-3.5 text-[#0088CC] mt-0.5 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-medium leading-tight">
                                  Tender Preparation Guide
                                </div>
                                <div className="text-[10px] text-[#64748B] mt-0.5">
                                  Generated {new Date(t.prep_guide.generated_at).toLocaleDateString()} · .docx
                                </div>
                              </div>
                            </div>
                          </a>
                        )}
                        {attachments.map((att, i) => (
                          <a
                            key={i}
                            href={`${API}/files/${att.storage_path}`}
                            target="_blank"
                            rel="noreferrer"
                            className="block rounded border border-[#DDE5EC] bg-white hover:border-[#0088CC]/50 hover:bg-[#0088CC]/[0.06] px-2.5 py-2 text-xs text-[#011836] transition-colors"
                            title={`Open ${att.filename}`}
                          >
                            <div className="flex items-start gap-2">
                              <FileText className="h-3.5 w-3.5 text-[#0088CC] mt-0.5 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-medium leading-tight">
                                  {att.filename || "untitled"}
                                </div>
                                {att.uploaded_at && (
                                  <div className="text-[10px] text-[#64748B] mt-0.5">
                                    {new Date(att.uploaded_at).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---------- Edit / Create dialog ---------- */}
      <Dialog open={modalOpen} onOpenChange={(o) => (o ? null : close())}>
        <DialogContent
          className="sos-surface text-[#011836] max-w-4xl max-h-[92vh] overflow-y-auto"
          data-testid="tender-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide">
              {editing ? "Edit tender" : "New tender"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Attachments + AI extract */}
            <div className="rounded-md border border-[#0088CC]/30 bg-[#E6F2FA]/30 p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-display text-sm font-semibold text-[#011836]">
                    {editing ? "Documents" : "Upload tender documents"}
                  </div>
                  <div className="text-xs text-[#5B6B7C] mt-0.5">
                    {editing
                      ? "Click a filename to open. Use Upload to add addenda or amendments."
                      : "Drag or pick PDF, Word, Excel, or images — multiple files at once is fine. Fields auto-fill from the first file; later files top up gaps without overwriting your edits."}
                  </div>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  multiple
                  accept=".pdf,.docx,.xlsx,.doc,.xls,.txt,.png,.jpg,.jpeg,.webp"
                  onChange={onUploadFile}
                  data-testid="tender-file-input"
                />
                <div className="flex items-center gap-2">
                  {editing && form.attachments.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        // `editing` is the tender id string (see useState
                        // declaration comment) — not a tender object, so
                        // avoid `.id` here (previously produced
                        // ".../tenders/undefined/attachments.zip").
                        const url = `${API}/tenders/${editing}/attachments.zip`;
                        // <a download> + cookie-auth — same pattern as the
                        // per-file <a href> elsewhere on this card.
                        const a = document.createElement("a");
                        a.href = url;
                        a.rel = "noopener";
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                      }}
                      className="border-[#0088CC]/40 text-[#0088CC] hover:bg-[#0088CC]/10"
                      data-testid="tender-download-all-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download all
                    </Button>
                  )}
            {canEdit && (
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
                data-testid="tender-upload-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading
                  ? extractingPath
                    ? "Analysing…"
                    : "Uploading…"
                  : "Upload files"}
              </Button>
            )}
                </div>
              </div>
              {form.attachments.length > 0 && (
                <div className="space-y-3">
                  {(() => {
                    // Group attachments by upload date with newest day first
                    // and newest file first within each day.
                    const sorted = [...form.attachments]
                      .map((a, originalIdx) => ({ ...a, _idx: originalIdx }))
                      .sort((x, y) =>
                        (y.uploaded_at || "").localeCompare(x.uploaded_at || "")
                      );
                    const groups = [];
                    for (const a of sorted) {
                      const dayKey = (a.uploaded_at || "").slice(0, 10) || "unknown";
                      let g = groups.find((gg) => gg.dayKey === dayKey);
                      if (!g) {
                        g = { dayKey, label: formatDayLabel(a.uploaded_at), items: [] };
                        groups.push(g);
                      }
                      g.items.push(a);
                    }
                    return groups.map((g) => (
                      <div key={g.dayKey} data-testid={`attachment-day-${g.dayKey}`}>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#5B6B7C] mb-1.5">
                          {g.label}
                        </div>
                        <div className="space-y-1.5">
                          {g.items.map((a) => {
                            const i = a._idx;
                            const href = `${API}/files/${a.storage_path}`;
                            return (
                              <div
                                key={a.storage_path + i}
                                className="flex items-center justify-between gap-2 rounded border border-[#DDE5EC] bg-white px-3 py-2"
                                data-testid={`tender-attachment-${i}`}
                              >
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="min-w-0 flex-1 flex items-center gap-2 group"
                                  data-testid={`tender-attachment-open-${i}`}
                                  title="Open document in a new tab"
                                >
                                  <FileText className="h-3.5 w-3.5 text-[#5B6B7C] shrink-0 group-hover:text-[#0088CC]" />
                                  <span className="text-sm text-[#011836] truncate group-hover:text-[#0088CC] group-hover:underline">
                                    {a.filename}
                                  </span>
                                  <span className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                                    {Math.round((a.size || 0) / 1024)} KB
                                  </span>
                                </a>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => runAiExtract(a)}
                                  disabled={extractingPath === a.storage_path}
                                  className="border-[#0088CC]/40 text-[#0088CC] hover:bg-[#0088CC]/10 disabled:opacity-50"
                                  data-testid={`tender-extract-${i}`}
                                >
                                  <Sparkles className="h-3 w-3 mr-1" />
                                  {extractingPath === a.storage_path ? "Reading…" : "Extract"}
                                </Button>
                                {canEdit && (
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(i)}
                                    className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                                    data-testid={`tender-attachment-remove-${i}`}
                                    aria-label="Remove attachment"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>

            {/* Ask the documents — natural-language Q&A grounded only in
                the uploaded attachments. Each answer comes back with
                citations (document + page + verbatim quote) so the team
                can verify in seconds. Only shown for saved tenders with
                at least one attachment. */}
            {editing && form.attachments && form.attachments.length > 0 && (
              <div
                className="rounded-md border border-[#0088CC]/30 bg-[#E6F2FA]/30 p-4 space-y-3"
                data-testid="tender-qa-panel"
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-[#0088CC]" />
                  <div className="font-display text-sm font-semibold text-[#011836]">
                    Ask the documents
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                    Answers pulled straight from {form.attachments.length} attached file
                    {form.attachments.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={qaInput}
                    onChange={(e) => setQaInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && !qaSending) {
                        e.preventDefault();
                        askQuestion();
                      }
                    }}
                    placeholder="e.g. When is the mandatory site visit? What insurance is required?"
                    className="bg-[#F6F7F8] border-[#DDE5EC] text-[#011836] text-sm focus-visible:ring-[#0088CC]"
                    data-testid="tender-qa-input"
                    disabled={qaSending}
                  />
                  <Button
                    type="button"
                    onClick={askQuestion}
                    disabled={qaSending || !qaInput.trim()}
                    className="bg-[#0088CC] hover:bg-[#006DA6] text-white shrink-0"
                    data-testid="tender-qa-ask-btn"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {qaSending ? "Thinking…" : "Ask"}
                  </Button>
                </div>
                {qaHistory.length === 0 && !qaSending && (
                  <div className="text-xs text-[#5B6B7C] py-1">
                    Type a question and hit Ask — the AI will answer using only
                    the uploaded documents and cite where it found each fact.
                  </div>
                )}
                {qaHistory.length > 0 && (
                  <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                    {qaHistory.map((qa) => (
                      <div
                        key={qa.id}
                        className="rounded border border-[#DDE5EC] bg-white p-3"
                        data-testid={`tender-qa-entry-${qa.id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#0088CC] font-display font-semibold mb-0.5">
                              Question
                            </div>
                            <div className="text-sm text-[#011836] leading-snug">
                              {qa.question}
                            </div>
                          </div>
                          {canEdit && (
                            <button
                              type="button"
                              onClick={() => deleteQa(qa.id)}
                              className="p-1 rounded text-[#64748B] hover:text-[#EF4444] hover:bg-[#FDECEC] shrink-0"
                              data-testid={`tender-qa-delete-${qa.id}`}
                              aria-label="Remove this Q&A"
                              title="Remove from history"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="mb-1">
                          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5B6B7C] font-display font-semibold mb-0.5">
                            Answer
                          </div>
                          <div className="text-sm text-[#011836] leading-relaxed whitespace-pre-line">
                            {qa.answer}
                          </div>
                        </div>
                        {Array.isArray(qa.citations) && qa.citations.length > 0 && (
                          <div className="mt-2.5 space-y-1.5">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5B6B7C] font-display font-semibold">
                              Sources
                            </div>
                            {qa.citations.map((c, i) => {
                              // Try to link to the source PDF if it's in the
                              // current attachment set
                              const att = (form.attachments || []).find(
                                (a) => (a.filename || "").toLowerCase() === (c.document || "").toLowerCase(),
                              );
                              const href = att ? `${API}/files/${att.storage_path}` : null;
                              return (
                                <div
                                  key={i}
                                  className="text-xs rounded border border-[#DDE5EC] bg-[#F6F7F8]/60 px-2.5 py-2"
                                >
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <FileText className="h-3 w-3 text-[#0088CC] shrink-0" />
                                    {href ? (
                                      <a
                                        href={href}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[#0088CC] hover:underline truncate font-medium"
                                        title={`Open ${c.document}`}
                                      >
                                        {c.document || "Source"}
                                      </a>
                                    ) : (
                                      <span className="text-[#011836] font-medium truncate">
                                        {c.document || "Source"}
                                      </span>
                                    )}
                                    {c.section && (
                                      <span className="text-[#5B6B7C]"> · {c.section}</span>
                                    )}
                                  </div>
                                  {c.quote && (
                                    <div className="flex gap-1.5 items-start text-[#33475C] leading-relaxed border-l-2 border-[#0088CC]/40 ml-1 pl-2">
                                      <Quote className="h-3 w-3 text-[#0088CC]/70 shrink-0 mt-0.5" />
                                      <span className="italic">{c.quote}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div className="mt-2 text-[10px] text-[#64748B]">
                          Asked by {qa.asked_by || "unknown"} · {formatLogTime(qa.asked_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Core fields — wrapped in a fieldset so view-only staff can
                browse every value but can't type into anything. The
                browser natively disables every input/textarea/select/
                button inside a `disabled` fieldset, which keeps this
                guard bulletproof against future inputs being added to
                the form without remembering to gate them individually.
                `className="contents"` removes the fieldset from layout
                flow so it's purely a semantic wrapper. */}
            <fieldset disabled={!canEdit} className="contents" data-testid="tender-form-fieldset">
            {/* Core fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  placeholder="e.g. Construction of two 25m crew transfer vessels"
                  data-testid="tender-title-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Client / Issuer</Label>
                <Input
                  value={form.client}
                  onChange={(e) => setForm({ ...form, client: e.target.value })}
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="tender-client-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Due date</Label>
                <Input
                  type="date"
                  value={form.due_date || ""}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="tender-due-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger
                    className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836]"
                    data-testid="tender-status-input"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                    {STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Estimated value</Label>
                <Input
                  value={form.estimated_value}
                  onChange={(e) => setForm({ ...form, estimated_value: e.target.value })}
                  placeholder='e.g. "$8M – $12M AUD"'
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="tender-value-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Submission method</Label>
                <Input
                  value={form.submission_method}
                  onChange={(e) => setForm({ ...form, submission_method: e.target.value })}
                  placeholder="Email / portal URL / postal address"
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="tender-submission-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Scope summary</Label>
                <Textarea
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  rows={4}
                  placeholder="What's being procured, operational context, and key boundaries — 3-5 sentences."
                  className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="tender-summary-input"
                />
              </div>
            </div>

            {/* Multiple vessels + trailer requirement — both pre-populated
                by the AI extractor. The vessels list is intentionally
                separate from `vessel_specs` (which only fires for
                single-vessel tenders). */}
            <div className="rounded-md border border-[#DDE5EC] bg-white p-3 space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="trailer_required"
                  checked={form.trailer_required === true}
                  onChange={(e) => setForm({ ...form, trailer_required: e.target.checked })}
                  className="h-4 w-4 rounded border-[#C4D0DB] bg-white text-[#0088CC] focus:ring-2 focus:ring-[#0088CC]/60 cursor-pointer"
                  data-testid="trailer-required-checkbox"
                />
                <label htmlFor="trailer_required" className="cursor-pointer select-none">
                  <div className="text-xs font-display font-semibold text-[#011836] uppercase tracking-wider">
                    Trailer required
                  </div>
                  <div className="text-[10px] text-[#5B6B7C] mt-0.5">
                    Auto-ticked if the AI detects road-transport requirements in the tender.
                  </div>
                </label>
              </div>
              <div className="border-t border-[#DDE5EC] pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                      Vessels in this tender
                    </Label>
                    <div className="text-[10px] text-[#5B6B7C] mt-1">
                      Used when the tender procures more than one vessel design. Each row = one short brief.
                    </div>
                  </div>
                  {canEdit && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          vessels: [
                            ...(f.vessels || []),
                            { label: `Vessel ${(f.vessels?.length || 0) + 1}`, summary: "" },
                          ],
                        }))
                      }
                      className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
                      data-testid="vessel-brief-add-btn"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1.5" /> Add vessel
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {(!form.vessels || form.vessels.length === 0) && (
                    <div className="text-xs text-[#5B6B7C]">
                      None. AI extract will populate these when the tender names more than one vessel.
                    </div>
                  )}
                  {(form.vessels || []).map((v, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-12 gap-2 items-center rounded-md border border-[#DDE5EC] bg-white p-3"
                      data-testid={`vessel-brief-row-${i}`}
                    >
                      <div className="col-span-3">
                        <Input
                          value={v.label || ""}
                          onChange={(e) => {
                            const arr = [...form.vessels];
                            arr[i] = { ...arr[i], label: e.target.value };
                            setForm({ ...form, vessels: arr });
                          }}
                          placeholder={`Vessel ${i + 1}`}
                          className="bg-white border-[#DDE5EC] text-[#011836] text-sm focus-visible:ring-[#0088CC]"
                          data-testid={`vessel-brief-label-${i}`}
                        />
                      </div>
                      <div className="col-span-8">
                        <Input
                          value={v.summary || ""}
                          onChange={(e) => {
                            const arr = [...form.vessels];
                            arr[i] = { ...arr[i], summary: e.target.value };
                            setForm({ ...form, vessels: arr });
                          }}
                          placeholder="12-14m patrol vessel with trailer"
                          className="bg-white border-[#DDE5EC] text-[#011836] text-sm focus-visible:ring-[#0088CC]"
                          data-testid={`vessel-brief-summary-${i}`}
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            const arr = form.vessels.filter((_, idx) => idx !== i);
                            setForm({ ...form, vessels: arr });
                          }}
                          className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                          data-testid={`vessel-brief-remove-${i}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Vessel specs — structured label/value rows (mirrors flyer particulars) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                    Vessel specs
                  </Label>
                  <div className="text-[10px] text-[#5B6B7C] mt-1">
                    Length, beam, draft, range, speed, engines — one row per spec, like the flyer&apos;s Principal Particulars.
                  </div>
                </div>
                {canEdit && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        vessel_specs: [...(f.vessel_specs || []), { label: "", value: "" }],
                      }))
                    }
                    className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
                    data-testid="vessel-spec-add-btn"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Add row
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {(!form.vessel_specs || form.vessel_specs.length === 0) && (
                  <div className="text-xs text-[#5B6B7C]">
                    Optional. AI extract will populate this from the uploaded tender if specs are present.
                  </div>
                )}
                {(form.vessel_specs || []).map((sp, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-12 gap-2 items-center rounded-md border border-[#DDE5EC] bg-white p-3"
                    data-testid={`vessel-spec-row-${i}`}
                  >
                    <div className="col-span-5">
                      <Input
                        value={sp.label || ""}
                        onChange={(e) =>
                          setForm((f) => {
                            const arr = [...f.vessel_specs];
                            arr[i] = { ...arr[i], label: e.target.value };
                            return { ...f, vessel_specs: arr };
                          })
                        }
                        placeholder="Length Overall"
                        className="bg-white border-[#DDE5EC] text-[#011836] h-9 focus-visible:ring-[#0088CC]"
                        data-testid={`vessel-spec-label-${i}`}
                      />
                    </div>
                    <div className="col-span-6">
                      <Input
                        value={sp.value || ""}
                        onChange={(e) =>
                          setForm((f) => {
                            const arr = [...f.vessel_specs];
                            arr[i] = { ...arr[i], value: e.target.value };
                            return { ...f, vessel_specs: arr };
                          })
                        }
                        placeholder="25.0 m"
                        className="bg-white border-[#DDE5EC] text-[#011836] h-9 focus-visible:ring-[#0088CC]"
                        data-testid={`vessel-spec-value-${i}`}
                      />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button
                        type="button"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            vessel_specs: f.vessel_specs.filter((_, j) => j !== i),
                          }))
                        }
                        className="p-1.5 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                        data-testid={`vessel-spec-remove-${i}`}
                        aria-label="Remove spec row"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <ChipList
              label="Deliverables"
              testidBase="deliverable"
              items={form.deliverables}
              onChange={(items) => setForm({ ...form, deliverables: items })}
            />
            <ChipList
              label="Required certifications / class"
              testidBase="certification"
              items={form.certifications}
              onChange={(items) => setForm({ ...form, certifications: items })}
            />
            <ChipList
              label="Risk flags"
              testidBase="risk"
              items={form.risk_flags}
              onChange={(items) => setForm({ ...form, risk_flags: items })}
              accent="#FBBF24"
            />

            {/* Key dates editor — auto-populated by AI extract, but fully
                hand-editable. Each row is one fixture: site visit, briefing,
                Q&A deadline, etc. */}
            <div className="rounded-md border border-[#FBBF24]/30 bg-[#FBBF24]/[0.04] p-4">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-[#FBBF24]" />
                  <div className="font-display text-sm font-semibold text-[#011836]">Key dates</div>
                  <span className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                    Mandatory meetings, site visits, deadlines
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {editing && canEdit && (form.attachments || []).length > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={refreshKeyDates}
                      disabled={refreshingKeyDates}
                      className="border-[#0088CC]/40 text-[#0088CC] hover:bg-[#0088CC]/10 disabled:opacity-50"
                      data-testid="tender-refresh-key-dates"
                      title="Re-scan every attachment and add any newly-found dates"
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1" />
                      {refreshingKeyDates ? "Scanning…" : "Refresh from docs"}
                    </Button>
                  )}
                  {canEdit && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                    setForm((f) => ({
                      ...f,
                      key_dates: [
                        ...(f.key_dates || []),
                        {
                          date: "",
                          label: "",
                          location: "",
                          notes: "",
                          mandatory: true,
                          register_url: "",
                          register_instructions: "",
                          source_document: "",
                          source_section: "",
                        },
                      ],
                    }))
                  }
                  className="border-[#FBBF24]/40 text-[#FBBF24] hover:bg-[#FBBF24]/10"
                  data-testid="tender-key-date-add"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add date
                </Button>
                  )}
                </div>
              </div>
              {(form.key_dates || []).length === 0 ? (
                <div className="text-xs text-[#5B6B7C] py-2">
                  No key dates yet. Upload tender docs and let AI extract them, or add one manually.
                </div>
              ) : (
                <div className="space-y-2">
                  {(form.key_dates || []).map((kd, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-12 gap-2 items-start rounded border border-[#DDE5EC] bg-white p-2.5"
                      data-testid={`tender-key-date-${i}`}
                    >
                      <Input
                        type="date"
                        value={(kd.date || "").slice(0, 10)}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            key_dates: f.key_dates.map((x, j) =>
                              j === i ? { ...x, date: e.target.value } : x
                            ),
                          }))
                        }
                        className="col-span-3 bg-[#F6F7F8] border-[#DDE5EC] text-[#011836] text-xs focus-visible:ring-[#FBBF24]"
                        data-testid={`tender-key-date-date-${i}`}
                      />
                      <Input
                        value={kd.label || ""}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            key_dates: f.key_dates.map((x, j) =>
                              j === i ? { ...x, label: e.target.value } : x
                            ),
                          }))
                        }
                        placeholder="e.g. Mandatory site visit"
                        className="col-span-4 bg-[#F6F7F8] border-[#DDE5EC] text-[#011836] text-xs focus-visible:ring-[#FBBF24]"
                        data-testid={`tender-key-date-label-${i}`}
                      />
                      <Input
                        value={kd.location || ""}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            key_dates: f.key_dates.map((x, j) =>
                              j === i ? { ...x, location: e.target.value } : x
                            ),
                          }))
                        }
                        placeholder="Location (optional)"
                        className="col-span-3 bg-[#F6F7F8] border-[#DDE5EC] text-[#011836] text-xs focus-visible:ring-[#FBBF24]"
                        data-testid={`tender-key-date-location-${i}`}
                      />
                      <div className="col-span-2 flex items-center justify-between gap-1">
                        <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#FBBF24] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={kd.mandatory !== false}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                key_dates: f.key_dates.map((x, j) =>
                                  j === i ? { ...x, mandatory: e.target.checked } : x
                                ),
                              }))
                            }
                            className="accent-[#FBBF24]"
                            data-testid={`tender-key-date-mandatory-${i}`}
                          />
                          Mandatory
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              key_dates: f.key_dates.filter((_, j) => j !== i),
                            }))
                          }
                          className="p-1 rounded text-[#5B6B7C] hover:text-[#EF4444] hover:bg-[#FDECEC]"
                          data-testid={`tender-key-date-remove-${i}`}
                          aria-label="Remove key date"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {kd.notes !== undefined && (
                        <Input
                          value={kd.notes || ""}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              key_dates: f.key_dates.map((x, j) =>
                                j === i ? { ...x, notes: e.target.value } : x
                              ),
                            }))
                          }
                          placeholder="Notes — RSVP details, dial-in, etc. (optional)"
                          className="col-span-12 bg-[#F6F7F8] border-[#DDE5EC] text-[#011836] text-xs focus-visible:ring-[#FBBF24]"
                          data-testid={`tender-key-date-notes-${i}`}
                        />
                      )}
                      {/* Registration row — how to register and where the
                          info came from. Optional but high-value. */}
                      <Input
                        value={kd.register_url || ""}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            key_dates: f.key_dates.map((x, j) =>
                              j === i ? { ...x, register_url: e.target.value } : x
                            ),
                          }))
                        }
                        placeholder="Registration link / portal URL / mailto: (optional)"
                        className="col-span-6 bg-[#F6F7F8] border-[#DDE5EC] text-[#011836] text-xs focus-visible:ring-[#FBBF24]"
                        data-testid={`tender-key-date-register-url-${i}`}
                      />
                      <Input
                        value={kd.register_instructions || ""}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            key_dates: f.key_dates.map((x, j) =>
                              j === i ? { ...x, register_instructions: e.target.value } : x
                            ),
                          }))
                        }
                        placeholder="How to register — who to email, what to include, lead time (optional)"
                        className="col-span-6 bg-[#F6F7F8] border-[#DDE5EC] text-[#011836] text-xs focus-visible:ring-[#FBBF24]"
                        data-testid={`tender-key-date-register-instructions-${i}`}
                      />
                      <Input
                        value={kd.source_document || ""}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            key_dates: f.key_dates.map((x, j) =>
                              j === i ? { ...x, source_document: e.target.value } : x
                            ),
                          }))
                        }
                        placeholder="Source document (e.g. Site-Visit-Briefing.pdf)"
                        className="col-span-6 bg-[#F6F7F8] border-[#DDE5EC] text-[#011836] text-xs focus-visible:ring-[#FBBF24]"
                        data-testid={`tender-key-date-source-doc-${i}`}
                      />
                      <Input
                        value={kd.source_section || ""}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            key_dates: f.key_dates.map((x, j) =>
                              j === i ? { ...x, source_section: e.target.value } : x
                            ),
                          }))
                        }
                        placeholder="Where in the doc (e.g. Section 3.2, page 14)"
                        className="col-span-6 bg-[#F6F7F8] border-[#DDE5EC] text-[#011836] text-xs focus-visible:ring-[#FBBF24]"
                        data-testid={`tender-key-date-source-section-${i}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Internal notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="mt-1.5 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                data-testid="tender-notes-input"
              />
            </div>

            {/* Subscribers */}
            <div className="rounded-md border border-[#DDE5EC] bg-white p-4">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-[#0088CC]" />
                <div className="font-display text-sm font-semibold text-[#011836]">Subscribers</div>
                <span className="text-[10px] uppercase tracking-wider text-[#5B6B7C]">
                  Emailed on create + update
                </span>
              </div>
              <div className="flex gap-2">
                <Input
                  value={subscriberInput}
                  onChange={(e) => setSubscriberInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSubscriber();
                    }
                  }}
                  placeholder="email@example.com"
                  className="flex-1 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                  data-testid="subscriber-input"
                />
                {canEdit && (
                  <Button
                    type="button"
                    onClick={addSubscriber}
                    variant="outline"
                    className="border-[#0088CC]/40 text-[#0088CC] hover:bg-[#0088CC]/10"
                    data-testid="subscriber-add-btn"
                  >
                    Add
                  </Button>
                )}
              </div>
              {form.subscribers.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {form.subscribers.map((s) => (
                    <span
                      key={s}
                      className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded bg-[#0088CC]/10 text-[#0088CC] border border-[#0088CC]/30"
                      data-testid={`subscriber-chip-${s}`}
                    >
                      {s}
                      <button
                        type="button"
                        onClick={() => removeSubscriber(s)}
                        className="text-[#0088CC]/70 hover:text-[#011836]"
                        aria-label={`Remove ${s}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-2 text-[10px] text-[#5B6B7C]">
                Note: Email delivery isn&apos;t wired up yet — notifications are logged on the server. Once you connect Resend, alerts will go out automatically.
              </div>
            </div>

            {/* Activity log — read-only audit trail */}
            {editing && (
              <div className="rounded-md border border-[#DDE5EC] bg-white p-4">
                <div className="font-display text-sm font-semibold text-[#011836] mb-2">
                  Activity history
                </div>
                {(form.history || []).length === 0 ? (
                  <div className="text-xs text-[#5B6B7C]">
                    No activity recorded yet for this tender.
                  </div>
                ) : (
                  <ol
                    className="relative border-l border-[#DDE5EC] ml-2 space-y-3"
                    data-testid="tender-history-list"
                  >
                    {[...(form.history || [])]
                      .sort((a, b) => (b.at || "").localeCompare(a.at || ""))
                      .map((h, i) => {
                        const meta = HISTORY_ACTION_META[h.action] || {
                          label: h.action || "Activity",
                          color: "#94A3B8",
                        };
                        return (
                          <li
                            key={`${h.at}-${i}`}
                            className="pl-4 relative"
                            data-testid={`tender-history-entry-${i}`}
                          >
                            <span
                              className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full"
                              style={{ backgroundColor: meta.color }}
                              aria-hidden
                            />
                            <div className="flex flex-wrap items-baseline gap-2">
                              <span
                                className="text-[10px] uppercase tracking-wider font-display font-semibold"
                                style={{ color: meta.color }}
                              >
                                {meta.label}
                              </span>
                              <span className="text-[10px] text-[#5B6B7C]">
                                {formatLogTime(h.at)}
                              </span>
                              {h.by && (
                                <span className="text-[10px] text-[#5B6B7C]">
                                  · by {h.by}
                                </span>
                              )}
                            </div>
                            {h.detail && (
                              <div className="text-xs text-[#33475C] mt-0.5 break-words">
                                {h.detail}
                              </div>
                            )}
                          </li>
                        );
                      })}
                  </ol>
                )}
              </div>
            )}
            </fieldset>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={close}
              className="border-[#DDE5EC] text-[#5B6B7C] hover:text-[#011836] hover:border-[#C4D0DB]"
              data-testid="tender-cancel-btn"
            >
              Cancel
            </Button>
            {canEdit && (
              <Button
                type="button"
                onClick={save}
                className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
                data-testid="tender-save-btn"
              >
                {editing ? "Save changes" : "Create tender"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Monday briefing delivery report */}
      <Dialog open={!!briefingReport} onOpenChange={(o) => !o && setBriefingReport(null)}>
        <DialogContent className="bg-white border-[#DDE5EC] text-[#011836] max-w-lg" data-testid="briefing-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Monday briefing — delivery report</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
            {(briefingReport || []).map((r) => (
              <div
                key={r.to}
                className="flex items-start justify-between gap-3 rounded border border-[#DDE5EC] bg-white px-3 py-2"
                data-testid={`briefing-report-row-${r.to}`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-[#011836] truncate">
                    {r.name || r.to}
                    {r.role && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-[#5B6B7C]">{r.role}</span>
                    )}
                  </div>
                  <div className="text-xs text-[#5B6B7C] truncate">{r.to}</div>
                  {r.error && (
                    <div className="mt-1 text-xs text-[#EF4444] break-words" data-testid={`briefing-report-error-${r.to}`}>
                      {r.error}
                    </div>
                  )}
                </div>
                {r.delivered ? (
                  <span className="shrink-0 text-xs font-semibold text-[#22C55E] border border-[#22C55E]/30 bg-[#22C55E]/10 rounded px-2 py-0.5">
                    Sent
                  </span>
                ) : (
                  <span className="shrink-0 text-xs font-semibold text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/10 rounded px-2 py-0.5">
                    Failed
                  </span>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-[#5B6B7C]">
            "Sent" means Resend accepted the email. If a recipient still doesn't see it, ask them to check spam
            for "Your Monday briefing".
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function FileSearchIcon() {
  return (
    <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#0088CC]/10 text-[#0088CC]">
      <FileText className="h-6 w-6" />
    </div>
  );
}

function ChipList({ label, items, onChange, testidBase, accent = "#0088CC" }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) return;
    onChange([...items, v]);
    setDraft("");
  };
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">{label}</Label>
      <div className="mt-1.5 flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          className="flex-1 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
          data-testid={`${testidBase}-input`}
          placeholder={`Add ${label.toLowerCase().replace(/required /, "")}…`}
        />
        <Button
          type="button"
          onClick={add}
          variant="outline"
          className="border-[#DDE5EC] text-[#5B6B7C] hover:text-[#011836]"
          data-testid={`${testidBase}-add-btn`}
        >
          Add
        </Button>
      </div>
      {items.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {items.map((it, i) => (
            <span
              key={`${it}-${i}`}
              className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border"
              style={{ borderColor: `${accent}55`, backgroundColor: `${accent}14`, color: accent }}
              data-testid={`${testidBase}-chip-${i}`}
            >
              {it}
              <button
                type="button"
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                className="opacity-70 hover:opacity-100"
                aria-label={`Remove ${it}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
